import { useRef, useState } from "react";

export const HUBSPOT_PORTAL_ID = "536213";
export const APPS_CONTACT_FORM_GUID = "7c8b5a87-25f6-4c99-90f7-499b309255e0";
export const SPAM_SUBMIT_MIN_MS = 2500;

export const LEAD_SUBMIT_ERROR =
  "We couldn't send that just now. Please try again, or email hello@maxiomtech.com.";

const CONTACT_OBJECT_TYPE = "0-1";

const KEYBOARD_ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];

export function splitName(fullName) {
  const cleaned = String(fullName || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!cleaned) return { firstname: "", lastname: "" };
  const parts = cleaned.split(" ");
  if (parts.length === 1) return { firstname: parts[0], lastname: "" };
  return { firstname: parts[0], lastname: parts.slice(1).join(" ") };
}

export function isGibberishName(name) {
  const raw = String(name || "").trim();
  if (!raw) return false;
  if (/https?:\/\/|www\.|<\s*script/i.test(raw)) return true;
  if (/\d/.test(raw)) return true;
  if (/(.)\1{3,}/i.test(raw)) return true;

  const lower = raw.toLowerCase();
  const tokens = lower.split(/\s+/).filter(Boolean);
  const letterRuns = tokens.map((token) => token.replace(/[^a-z]/g, ""));

  if (hasKeyboardWalk(letterRuns.join(""))) return true;

  for (const letters of letterRuns) {
    if (!letters) continue;
    if (hasKeyboardWalk(letters)) return true;
    if (letters.length >= 4 && !/[aeiouy]/.test(letters)) return true;
    if (/[bcdfghjklmnpqrstvwxz]{5,}/.test(letters)) return true;
    if (letters.length >= 24) return true;
  }

  return false;
}

function hasKeyboardWalk(letters) {
  if (!letters || letters.length < 4) return false;
  for (const row of KEYBOARD_ROWS) {
    const reversed = row.split("").reverse().join("");
    for (let size = 4; size <= row.length; size += 1) {
      for (let i = 0; i <= row.length - size; i += 1) {
        const slice = row.slice(i, i + size);
        const back = reversed.slice(i, i + size);
        if (letters.includes(slice) || letters.includes(back)) return true;
      }
    }
  }
  return false;
}

export function isSpamSubmission({
  companyUrl = "",
  mountedAt,
  name = "",
  now = Date.now(),
} = {}) {
  if (String(companyUrl || "").trim()) return true;
  if (typeof mountedAt === "number" && now - mountedAt < SPAM_SUBMIT_MIN_MS) {
    return true;
  }
  if (isGibberishName(name)) return true;
  return false;
}

export function honeypotValue(event, stateValue = "") {
  let fromDom = "";
  const form = event?.currentTarget;
  if (form && typeof FormData !== "undefined") {
    try {
      fromDom = String(new FormData(form).get("company_url") || "");
    } catch {
      fromDom = "";
    }
  }
  return String(stateValue || fromDom || "").trim();
}

export function composeLeadMessage({
  message = "",
  budget = "",
  landingTitle = "",
  landingSlug = "",
  results = "",
  source = "",
} = {}) {
  const blocks = [];
  const note = String(message || "").trim();
  if (note) blocks.push(note);

  const details = [];
  if (source) details.push(`Form: ${source}`);
  if (landingTitle) details.push(`Landing title: ${landingTitle}`);
  if (landingSlug) details.push(`Landing slug: ${landingSlug}`);
  if (budget) details.push(`Budget: ${budget}`);
  const resultText = String(results || "").trim();
  if (resultText) details.push(resultText);
  if (details.length) blocks.push(details.join("\n"));

  return blocks.join("\n\n");
}

export function readHubspotUtk() {
  if (typeof document === "undefined" || !document.cookie) return "";
  const parts = document.cookie.split("; ");
  for (const part of parts) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    if (part.slice(0, eq) === "hubspotutk") {
      try {
        return decodeURIComponent(part.slice(eq + 1));
      } catch {
        return part.slice(eq + 1);
      }
    }
  }
  return "";
}

function contactField(name, value) {
  const trimmed = value == null ? "" : String(value).trim();
  if (!trimmed) return null;
  return { objectTypeId: CONTACT_OBJECT_TYPE, name, value: trimmed };
}

export function buildHubspotSubmission(lead, now = Date.now()) {
  const split = splitName(lead?.name || "");
  const fields = [
    contactField("email", lead?.email),
    contactField("firstname", lead?.firstname || split.firstname),
    contactField("lastname", lead?.lastname || split.lastname),
    contactField("company", lead?.company),
    contactField("phone", lead?.phone),
    contactField("message", lead?.message),
  ].filter(Boolean);

  const context = {
    pageUri: typeof location !== "undefined" ? location.href : "",
    pageName: typeof document !== "undefined" ? document.title : "",
  };
  const hutk = readHubspotUtk();
  if (hutk) context.hutk = hutk;

  return {
    submittedAt: now,
    fields,
    context,
  };
}

export function hubspotSubmitUrl(guid = APPS_CONTACT_FORM_GUID) {
  return `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${guid}`;
}

export async function submitHubspotForm(lead, { fetchImpl, guid, now } = {}) {
  const payload = buildHubspotSubmission(lead, now);
  const doFetch = fetchImpl || fetch;
  const response = await doFetch(hubspotSubmitUrl(guid), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let detail = "";
    try {
      detail = await response.text();
    } catch {
      detail = "";
    }
    const error = new Error(
      `HubSpot form submit failed with status ${response.status}`,
    );
    error.status = response.status;
    error.detail = detail;
    throw error;
  }

  return response;
}

// "sent" after a 2xx HubSpot response. "spam" skips the request.
// Throws when HubSpot returns a non-2xx response or the network fails.
export async function submitAppsContact(lead, guard = {}) {
  if (
    isSpamSubmission({
      companyUrl: guard.companyUrl,
      mountedAt: guard.mountedAt,
      name: guard.name ?? lead?.name,
      now: guard.now,
    })
  ) {
    return "spam";
  }

  await submitHubspotForm({
    email: lead?.email,
    name: lead?.name,
    company: lead?.company,
    phone: lead?.phone,
    message: composeLeadMessage(lead || {}),
  });
  return "sent";
}

export function useLeadFormGuard() {
  const mountedAtRef = useRef(Date.now());
  const [companyUrl, setCompanyUrl] = useState("");
  return {
    companyUrl,
    setCompanyUrl,
    mountedAt: mountedAtRef.current,
  };
}

export function FormMountMark({ mountedAtRef }) {
  if (mountedAtRef && mountedAtRef.current == null) {
    mountedAtRef.current = Date.now();
  }
  return null;
}

export function HoneypotField({ value, onChange }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        left: "-10000px",
        top: 0,
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      <label>
        Company URL
        <input
          type="text"
          name="company_url"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ width: "1px", height: "1px", border: 0, padding: 0, margin: 0 }}
        />
      </label>
    </div>
  );
}

export function LeadFormError({ message, testId }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-sm text-red-400" data-testid={testId}>
      {message}
    </p>
  );
}
